import { supabase } from "../lib/supabase";

export const checkApplicant = async (email) => {
  try {
    const { data, error } = await supabase
      .from("applicants")
      .select("*")
      .eq("email", email)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (err) {
    console.error("Error checking applicant:", err);
    return { data: null, error: err.message };
  }
};

export const createApplicant = async (formData) => {
  try {
    const {
      full_name,
      email,
      nsave_id,
      age,
      skills,
      field,
      gender,
      referral,
      // ai_usage_agreement and nsave_registration_confirmed are validated in frontend only
    } = formData;

    // Check if applicant already exists
    const { data: existingApplicant } = await checkApplicant(email);
    console.log(existingApplicant);
    if (existingApplicant) {
      return {
        success: false,
        message: "لقد سجلت مسبقًا",
      };
    }

    // Insert new applicant (without ai_usage_agreement and nsave_registration_confirmed)
    const { data, error } = await supabase
      .from("applicants")
      .insert([
        {
          full_name,
          email,
          nsave_id,
          age: parseInt(age),
          skills,
          field,
          gender,
          referral: referral || null,
          status: "قيد المراجعة",
        },
      ])
      .select()
      .single();

    if (error) {
      throw error;
    }

    return {
      success: true,
      message: "تم التسجيل بنجاح! سيتم مراجعة طلبك قريبًا.",
      data,
    };
  } catch (err) {
    console.error("Error creating applicant:", err);
    return {
      success: false,
      message: "حدث خطأ أثناء التسجيل. يرجى المحاولة مرة أخرى.",
    };
  }
};

export const updateApplicantStatus = async (applicantId, email, status) => {
  try {
    const { data, error } = await supabase
      .from("applicants")
      .update({ status })
      .eq("id", applicantId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    // Send email notification using Resend
    try {
      const acceptMessage = `
        <html dir="rtl">
          <body style="font-family: Arial, sans-serif; text-align: right;">
            <h1 style="font-size: 24px; font-weight: bold; padding: 10px;">شكرا لتقديمك في هاكاثون قمة</h1>
            <h2 style="font-size: 16px; font-weight: bold; padding: 10px;">تم قبولك للمشاركة في الهاكاثون 🎉</h2>
<p style="font-size: 14px; padding: 10px;">يجب عليك الان انشاء فريقك من خلال الرابط (حتى لو كنت منفردًا) <br/> https://qimma.dev/team</p>
<p style="font-size: 12px; padding: 10px;">ملاحظة: يجب وضع الايميل الخاص بك اولًا ثم باقي الاعضاء</p>        
<p style="font-size: 12px; padding: 10px;">لاتنسى الدخول الى سيرفر الديسكورد لمتابعة المستجدات والتواصل مع المشاركين <br/> https://discord.gg/jpRGNsyfcw</p>
</body>
        </html>
      `;

      const rejectMessage = `
        <html dir="rtl">
          <body style="font-family: Arial, sans-serif; text-align: right;">
            <h1 style="font-size: 24px; font-weight: bold; padding: 10px;">شكرا لتقديمك في هاكاثون قمة</h1>
            <h2 style="font-size: 16px; font-weight: bold; padding: 10px;">نأسف لعدم قبولك للمشاركة في الهاكاثون.</h2>
            <p style="font-size: 14px; padding: 10px;">حظًا أوفر في النسخ القادمة</p>
          </body>
        </html>
      `;

      // Call the API endpoint to send email
      const apiUrl =
        process.env.REACT_APP_API_URL || "https://api.qimma.dev/api/email/send";

      const emailResponse = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          to: email,
          subject: "نتيجة تقديمك في هاكاثون قمة",
          html: status === "مقبول" ? acceptMessage : rejectMessage,
        }),
      });

      const emailResult = await emailResponse.json();

      if (!emailResponse.ok) {
        console.error("Email API error:", emailResult);
      } else {
        console.log("Email sent successfully:", emailResult);
      }
    } catch (emailError) {
      console.error("Error sending email via Resend:", emailError);
      // Don't fail the entire operation if email fails
    }

    return {
      success: true,
      message: `تم تحديث حالة المتقدم إلى ${status}`,
      data,
    };
  } catch (err) {
    console.error("Error updating applicant status:", err);
    return {
      success: false,
      message: "حدث خطأ أثناء تحديث حالة المتقدم",
    };
  }
};

export const getAllApplicants = async (filters = {}) => {
  try {
    let query = supabase
      .from("applicants")
      .select("*")
      .order("created_at", { ascending: false });

    // Apply filters
    if (filters.field) {
      query = query.eq("field", filters.field);
    }
    if (filters.gender) {
      query = query.eq("gender", filters.gender);
    }
    if (filters.status) {
      query = query.eq("status", filters.status);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return {
      success: true,
      data: data || [],
    };
  } catch (err) {
    console.error("Error fetching applicants:", err);
    return {
      success: false,
      message: "حدث خطأ أثناء جلب بيانات المتقدمين",
      data: [],
    };
  }
};

export const getApplicantStats = async () => {
  try {
    const { data, error } = await supabase
      .from("applicants")
      .select("status, field, gender, age, referral");

    if (error) {
      throw error;
    }

    const stats = {
      total: data.length,
      accepted: data.filter((a) => a.status === "مقبول").length,
      rejected: data.filter((a) => a.status === "مرفوض").length,
      pending: data.filter((a) => a.status === "قيد المراجعة").length,
      averageAge:
        data.reduce((sum, a) => sum + (a.age || 0), 0) / data.length || 0,
      byField: {},
      byGender: {},
      byReferral: {},
      totalWithReferral: data.filter((a) => a.referral).length,
    };

    // Group by field
    data.forEach((applicant) => {
      const field = applicant.field || "غير محدد";
      stats.byField[field] = (stats.byField[field] || 0) + 1;
    });

    // Group by gender
    data.forEach((applicant) => {
      const gender = applicant.gender || "غير محدد";
      stats.byGender[gender] = (stats.byGender[gender] || 0) + 1;
    });

    // Group by referral
    data.forEach((applicant) => {
      if (applicant.referral) {
        const referral = applicant.referral;
        stats.byReferral[referral] = (stats.byReferral[referral] || 0) + 1;
      }
    });

    return {
      success: true,
      data: stats,
    };
  } catch (err) {
    console.error("Error fetching applicant stats:", err);
    return {
      success: false,
      message: "حدث خطأ أثناء جلب إحصائيات المتقدمين",
      data: null,
    };
  }
};
